import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import { createSelector } from 'reselect';
import { Chart } from '../Chart';
import { commonSettings } from '../charts.constants';
import { theme } from '../../../../theme/theme';
import { useChartSettings } from '../hooks/useChartSettings';

export const NETWORTH_CHART_CONTAINER_ID = 'nwc_chart';

export const netWorthChartSettings: Highcharts.Options = {
  ...commonSettings,
  chart: {
    spacingTop: 20,
    spacingRight: 20,
    style: {
      fontFamily: theme.fontFamily,
    },
  },

  plotOptions: {
    column: {
      stacking: 'normal',
      borderRadius: theme.radii.medium,
    },
  },
};

export const metricPrefixer = (value: number): string => {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  if (value === 0) {
    return '$0';
  }
  if (value <= -1_000_000) {
    return `$${((value / 1_000_000) * -1).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (value <= -1_000) {
    return `$${((value / 1_000) * -1).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return `$${value}`;
};

export const formatNetWorthYAxisLabel = (value: number): string | undefined => {
  if (value === 0) {
    return '$0';
  }
  if (value <= -1_000) {
    return `(${metricPrefixer(Math.abs(value))})`;
  }
  if (value >= 1_000) {
    return metricPrefixer(value);
  }
  return undefined;
};

export interface NetWorthHistoryRow {
  Title: string;
  Data: {
    Categories: string[];
    Series: {
      Assets: number[];
      Liabilities: number[];
      NetWorth: number[];
    };
  };
}

const EMPTY_NET_WORTH_SERIES: Highcharts.SeriesOptionsType[] = [
  { type: 'column', id: 'assets', name: 'ASSETS', color: '#09D2BD', data: [], legendIndex: 1 },
  {
    type: 'column',
    id: 'liabilities',
    name: 'LIABILITIES',
    color: '#FF836C',
    data: [],
    legendIndex: 2,
  },
  {
    type: 'line',
    id: 'netWorth',
    name: 'NETWORTH',
    color: '#8F959F',
    data: [],
    legendIndex: 0,
    marker: {
      radius: 3,
      fillColor: 'white',
      lineWidth: 2,
      lineColor: '#8F959F',
    },
  },
];

function buildNetWorthSeriesFromRow(row: NetWorthHistoryRow): Highcharts.SeriesOptionsType[] {
  const { Assets, Liabilities, NetWorth } = row.Data.Series;
  return [
    {
      type: 'column',
      id: 'assets',
      name: 'ASSETS',
      color: '#09D2BD',
      data: Assets,
      legendIndex: 1,
    },
    {
      type: 'column',
      id: 'liabilities',
      name: 'LIABILITIES',
      color: '#FF836C',
      data: Liabilities,
      legendIndex: 2,
    },
    {
      type: 'line',
      id: 'netWorth',
      name: 'NETWORTH',
      color: '#8F959F',
      data: NetWorth,
      legendIndex: 0,
      marker: {
        radius: 3,
        fillColor: 'white',
        lineWidth: 2,
        lineColor: '#8F959F',
      },
    },
  ];
}

function buildNetWorthCustomSettings(categories: string[]): Record<string, any> {
  const baseXAxis: Highcharts.XAxisOptions | undefined = Array.isArray(commonSettings.xAxis)
    ? commonSettings.xAxis[0]
    : commonSettings.xAxis;
  const baseYAxis: Highcharts.YAxisOptions | undefined = Array.isArray(commonSettings.yAxis)
    ? commonSettings.yAxis[0]
    : commonSettings.yAxis;

  return {
    legend: {
      ...(commonSettings.legend || {}),
      margin: 16,
    },
    yAxis: {
      ...baseYAxis,
      labels: {
        ...(baseYAxis?.labels || {}),
        formatter() {
          return formatNetWorthYAxisLabel(this.value as number);
        },
      },
    },
    xAxis: {
      ...baseXAxis,
      categories,
      labels: {
        ...(baseXAxis?.labels || {}),
        autoRotation: [-45],
        autoRotationLimit: 80,
        style: {
          ...(baseXAxis?.labels?.style || {}),
          fontFamily: theme.fontFamily,
          lineHeight: '16px',
        },
      },
    },
    tooltip: {
      ...(commonSettings.tooltip || {}),
      useHTML: true,
      shared: true,
      formatter() {
        const netWorthPoint = this.points?.find(
          point => point.series.userOptions?.id === 'netWorth',
        );
        const assetsPoint = this.points?.find(point => point.series.userOptions?.id === 'assets');
        const liabilitiesPoint = this.points?.find(
          point => point.series.userOptions?.id === 'liabilities',
        );
        const renderRow = (marker: string, label: string, value: string) => `
          <div style="display:flex;align-items:center;gap:6px;margin:0;line-height:16px;font-size:12px;font-weight:400;">
            ${marker}
            <span>${label}</span>
            <span>${value}</span>
          </div>
        `;

        return `
            <div style="font-size:12px;font-weight:400;line-height:16px;">
              ${renderRow(
                '<span style="display:inline-block;width:10px;height:2px;background:#8F959F;border-radius:1px;"></span>',
                'NETWORTH',
                metricPrefixer(Math.abs(netWorthPoint?.y || 0)),
              )}
              ${renderRow(
                '<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#09D2BD;"></span>',
                'ASSETS',
                metricPrefixer(assetsPoint?.y || 0),
              )}
              ${renderRow(
                '<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#FF836C;"></span>',
                'LIABILITIES',
                metricPrefixer(Math.abs(liabilitiesPoint?.y || 0)),
              )}
            </div>
          `;
      },
    },
  };
}

export interface NetWorthChartData {
  series: Highcharts.SeriesOptionsType[];
  categories: string[];
}

const selectNetWorthHistoryRowForRange = (
  state: { NetWorthReport?: Record<string, unknown> },
  clientId: string,
  range: string,
): NetWorthHistoryRow | null => {
  const netWorthReport = state.NetWorthReport?.[clientId] as
    | { NetWorth?: { History?: NetWorthHistoryRow[] } }
    | undefined;

  if (!netWorthReport?.NetWorth?.History) {
    return null;
  }

  const row = netWorthReport.NetWorth.History.find(item => item.Title === range);

  if (!row?.Data?.Series) {
    return null;
  }

  return row;
};

export const getNetWorthHistoryChartData = createSelector(
  [selectNetWorthHistoryRowForRange],
  (row): NetWorthChartData | null => {
    if (!row) {
      return null;
    }
    return {
      series: buildNetWorthSeriesFromRow(row),
      categories: row.Data.Categories,
    };
  },
);

export interface NetWorthChartProps {
  data: NetWorthChartData | null;
}

export function NetWorthChart({ data }: NetWorthChartProps) {
  const series = data?.series ?? EMPTY_NET_WORTH_SERIES;
  const customSettings = useMemo(() => buildNetWorthCustomSettings(data?.categories ?? []), [
    data?.categories,
  ]);

  const options = useChartSettings({
    series,
    settings: netWorthChartSettings,
    customSettings,
  });

  if (!data) {
    return (
      <div id="nwc_reactor">
        <div className="empty">
          <p>No net worth history data available for past 30 days.</p>
        </div>
      </div>
    );
  }

  return <Chart options={options} containerProps={{ id: NETWORTH_CHART_CONTAINER_ID }} />;
}
